import { useMemo, useCallback } from 'react'

import { Marketplace, Money, Placeholder, Save, Shield } from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import type { DeviceCatalogView, FilterSection } from 'modules/it_management/hooks/device_catalog/types'
import {
  CATEGORY_ICONS,
  CATEGORY_IDS,
  CATEGORY_LABELS,
} from 'modules/it_management/hooks/device_catalog/constants'
import type { MarketplaceSidebarSection } from 'modules/it_management/components/device_catalog/MarketplaceSidebar'

type UseSidebarSectionsArgs = {
  activeView: DeviceCatalogView
  selectedCategory: string | null
  savedCount: number
  attributeFilters: Record<string, string[]>
  dynamicFilterOptions: FilterSection[]
  setView: (view: DeviceCatalogView) => void
  setCategory: (category: string) => void
  setAttributeFilter: (key: string, values: string[]) => void
}

export function useSidebarSections({
  activeView,
  selectedCategory,
  savedCount,
  attributeFilters,
  dynamicFilterOptions,
  setView,
  setCategory,
  setAttributeFilter,
}: UseSidebarSectionsArgs) {
  // Filter sections come from useDeviceCatalogFilters.extractFilterOptions (delivery time, brand, attributes)
  const allFilterSections: FilterSection[] = useMemo(
    () => dynamicFilterOptions,
    [dynamicFilterOptions]
  )

  // Check if any filters are active
  const hasActiveFilters = Object.values(attributeFilters).some((v) => v.length > 0)

  const clearAllFilters = useCallback(() => {
    Object.keys(attributeFilters).forEach((key) => {
      setAttributeFilter(key, [])
    })
  }, [attributeFilters, setAttributeFilter])

  // Active sidebar item
  const activeItemId = useMemo(() => {
    if (activeView === 'category' && selectedCategory) {
      return selectedCategory
    }
    return activeView
  }, [activeView, selectedCategory])

  // Contextual search placeholder
  const searchPlaceholder = useMemo(() => {
    if (activeView === 'category' && selectedCategory) {
      return i18n.t('it_management.device_catalog.search_in_category', {
        category: CATEGORY_LABELS[selectedCategory]?.() ?? selectedCategory,
      })
    }
    return i18n.t('it_management.device_catalog.search_placeholder')
  }, [activeView, selectedCategory])

  // Build sidebar sections
  const sections: MarketplaceSidebarSection[] = useMemo(
    () => [
      {
        type: 'navigation' as const,
        items: [
          {
            id: 'all',
            label: i18n.t('it_management.device_catalog.views.all_devices'),
            icon: Marketplace,
            onClick: () => setView('all'),
          },
          {
            id: 'saved',
            label: i18n.t('it_management.device_catalog.views.saved'),
            icon: Save,
            badge: savedCount || undefined,
            onClick: () => setView('saved'),
          },
          {
            id: 'offers',
            label: i18n.t('it_management.device_catalog.views.offers'),
            icon: Money,
            onClick: () => setView('offers'),
          },
          {
            id: 'zero-touch',
            label: i18n.t('it_management.device_catalog.views.zero_touch'),
            icon: Shield,
            onClick: () => setView('zero-touch'),
          },
        ],
      },
      {
        type: 'categories' as const,
        title: i18n.t('it_management.device_catalog.categories.label'),
        items: CATEGORY_IDS.map((catId) => ({
          id: catId,
          label: CATEGORY_LABELS[catId]?.() ?? catId,
          icon: CATEGORY_ICONS[catId] ?? Placeholder,
          onClick: () => setCategory(catId),
        })),
      },
      {
        type: 'filters' as const,
        sections: allFilterSections,
        onClearAll: hasActiveFilters ? clearAllFilters : undefined,
      },
    ],
    [setView, setCategory, savedCount, allFilterSections, hasActiveFilters, clearAllFilters]
  )

  return { sections, activeItemId, searchPlaceholder }
}
